import dayjs from "dayjs";
import { listInviteTasksByRequesters } from "../../invites/services/inviteTaskService";
import { searchTeleconsultoriaAppointmentEntries } from "../../services/teleconsultoriaAppointmentsService";
import { isTeleconsultoriaParticipant } from "../../utils/actors";
import { patientParticipantActor } from "../../utils/teleconsultoriaAppointment";
import {
  bookingInviteState,
  latestInviteByAppointment,
  type BookingInviteState,
} from "../helpers/bookingInviteStatus";

/** Teleconsultoria marcada pela afiliada (lista "Teleconsultorias marcadas"). */
export interface AffiliateTeleconsultoriaRow {
  appointment: fhir5.Appointment;
  appointmentId: string;
  start?: string;
  patientName: string;
  teleconsultorName: string;
  requesterName: string;
  requesterReference: string;
  /** Situação do convite ao teleconsultor (aguardando, aceita, recusada...). */
  invite: BookingInviteState;
}

function patientName(appointment: fhir5.Appointment, patientsById: Map<string, fhir5.Patient>) {
  const actor = patientParticipantActor(appointment);
  if (actor?.display) return actor.display;
  const patient = patientsById.get(actor?.reference?.split("/")[1] || "");
  return patient?.name?.[0]?.text || "Paciente";
}

/** Convites dos solicitantes. Falha não derruba a lista: o status fica "Aguardando aceite". */
async function invitesOfRequesters(requesterRoleReferences: string[]): Promise<fhir5.Task[]> {
  try {
    return await listInviteTasksByRequesters(requesterRoleReferences);
  } catch (error) {
    console.error("affiliateTeleconsultoriasService.invitesOfRequesters", error);
    return [];
  }
}

/**
 * Teleconsultorias em que os profissionais da afiliada são solicitantes.
 * Período opcional (YYYY-MM-DD); sem ele, de hoje em diante.
 * Busca no formato do dashboard (actor=Practitioner...) e cruza
 * com os convites (Task) para mostrar se o teleconsultor já aceitou.
 */
export async function listAffiliateTeleconsultorias(params: {
  practitionerReferences: string;
  requesterRoleReferences: string[];
  startDate?: string;
  endDate?: string;
}): Promise<AffiliateTeleconsultoriaRow[]> {
  const [entries, inviteTasks] = await Promise.all([
    searchTeleconsultoriaAppointmentEntries({
      practitionerReferences: params.practitionerReferences,
      startDate: params.startDate || dayjs().format("YYYY-MM-DD"),
      ...(params.endDate && { endDate: params.endDate }),
      statuses: ["booked", "pending"],
    }),
    invitesOfRequesters(params.requesterRoleReferences),
  ]);
  const latestInvites = latestInviteByAppointment(inviteTasks);
  const resources = entries.map((entry) => entry.resource);
  const patientsById = new Map(
    resources
      .filter((resource): resource is fhir5.Patient => resource?.resourceType === "Patient")
      .map((patient) => [patient.id || "", patient]),
  );

  return resources
    .filter((resource): resource is fhir5.Appointment => resource?.resourceType === "Appointment")
    .filter((appointment) => Boolean(appointment.id))
    .map((appointment) => {
      // Host = 1º Practitioner sem CON (o teleconsultor só entra ao aceitar).
      const host = (appointment.participant || []).find(
        (item) =>
          item.actor?.reference?.includes("Practitioner/") && !isTeleconsultoriaParticipant(item),
      )?.actor;
      const invite = bookingInviteState(appointment, latestInvites.get(appointment.id as string));
      return {
        appointment,
        appointmentId: appointment.id as string,
        start: appointment.start,
        patientName: patientName(appointment, patientsById),
        teleconsultorName: invite.teleconsultorName || "Teleconsultor",
        requesterName: host?.display || "Profissional",
        requesterReference: host?.reference || "",
        invite,
      };
    })
    .sort((first, second) => dayjs(first.start).valueOf() - dayjs(second.start).valueOf());
}
